/**
 * Host 端：把一個 Port 接到 Bus。對應 docs/wire.md。
 *
 * 一個 app 實例一個 HostEndpoint。因此 Port 就是天然的權限邊界——
 * 未來要限制某個 app 只能存取自己的子樹（protocol.md P4），
 * 在這裡加一層路徑過濾即可，Bus 完全不用改。
 */

import type { Bus } from './bus.ts'
import {
  ON_CALL_TIMEOUT_MS,
  ON_WRITE_TIMEOUT_MS,
  SUPPORTED_WIRE_VERSIONS,
  WIRE_MAGIC,
  busError,
  isFrame,
  negotiate,
} from '@qdesktop/protocol'
import type {
  BusError,
  CallArgs,
  CallResult,
  Frame,
  GesturePhase,
  GesturePoint,
  NodeDef,
  Port,
  RegisterArgs,
  Registration,
  Unsubscribe,
  WriteArgs,
  WriteOutcome,
  WriteResult,
} from '@qdesktop/protocol'

export interface HostEndpointOptions {
  bus: Bus
  port: Port
  /** 由 host 指派。App 不能自選，避免搶佔他人命名空間。 */
  uid: string
  supportedProtocols?: readonly string[]
  kernelVersion?: string
  /** 這個 endpoint 內部出錯時通知 host（非 app 的錯誤） */
  onFault?: (error: BusError) => void
  /**
   * App 回報「有人碰了我的畫面」。跨來源 iframe 的指標事件出不來，這是宿主唯一
   * 問得到的來源。純通知——不碰任何 topic，宿主要不要理它是宿主的事。
   */
  onTouch?: () => void
  /**
   * 兩指手勢。**一根手指是 app 的，兩根是桌面的**——見 wire.md 的 `gesture`。
   *
   * 座標是螢幕座標，所以宿主只要看差值，不必知道那個 iframe 現在在哪。
   */
  onGesture?: (phase: GesturePhase, points: readonly GesturePoint[]) => void
}

interface Pending {
  resolve: (value: unknown) => void
  reject: (error: Error) => void
  timer: ReturnType<typeof setTimeout>
}

export class HostEndpoint {
  private readonly bus: Bus
  private readonly port: Port
  private readonly uid: string
  private readonly prefix: string
  private readonly supported: readonly string[]
  private readonly kernelVersion: string
  private readonly onFault: ((error: BusError) => void) | null
  private readonly onTouch: (() => void) | null
  private readonly onGesture:
    | ((phase: GesturePhase, points: readonly GesturePoint[]) => void)
    | null

  private readonly subs = new Map<number, Unsubscribe>()
  private readonly pending = new Map<string, Pending>()
  private registration: Registration | null = null
  private protocol: string | null = null
  private nextSubId = 1
  private nextReqId = 1
  private detached = false

  private constructor(options: HostEndpointOptions) {
    this.bus = options.bus
    this.port = options.port
    this.uid = options.uid
    this.prefix = `app/${options.uid}`
    this.supported = options.supportedProtocols ?? SUPPORTED_WIRE_VERSIONS
    this.kernelVersion = options.kernelVersion ?? '0.1.0'
    this.onFault = options.onFault ?? null
    this.onTouch = options.onTouch ?? null
    this.onGesture = options.onGesture ?? null
  }

  static attach(options: HostEndpointOptions): HostEndpoint {
    const endpoint = new HostEndpoint(options)
    options.port.onMessage((data) => endpoint.handle(data))
    options.port.onClose(() => endpoint.detach('iframe 已卸載或崩潰'))
    return endpoint
  }

  /** Host 主動結束：送 bye 並關閉 Port */
  close(reason = '已關閉'): void {
    if (this.detached) return
    this.send({ qd: WIRE_MAGIC, t: 'bye', reason })
    this.port.close()
  }

  // ─────────────────────────────── 收訊

  private handle(data: unknown): void {
    if (this.detached) return
    if (!isFrame(data)) return // §2 靜默丟棄非本協定訊息

    switch (data.t) {
      case 'hello':
        this.handleHello(data.accepts, data.sdk)
        return
      case 'req':
        void this.handleReq(data.id, data.op, data.a)
        return
      case 'note':
        this.handleNote(data.op, data.a)
        return
      case 'ok':
        this.settle(data.id, null, data.r)
        return
      case 'err':
        this.settle(data.id, data.e, undefined)
        return
      default:
        // §11-2：未知類型以 err 回覆，不得斷線。無 id 可回時忽略。
        return
    }
  }

  private handleHello(accepts: unknown, _sdk: unknown): void {
    const list = Array.isArray(accepts) ? accepts.filter((v) => typeof v === 'string') : []
    const chosen = negotiate(list, this.supported)
    if (!chosen) {
      this.send({
        qd: WIRE_MAGIC,
        t: 'refuse',
        e: busError('E_UNSUPPORTED', '沒有共同支援的協定版本', {
          hint: `此桌面支援：${this.supported.join(', ')}`,
          details: { accepts: list },
        }),
      })
      this.port.close()
      return
    }
    this.protocol = chosen
    this.send({
      qd: WIRE_MAGIC,
      t: 'welcome',
      protocol: chosen,
      uid: this.uid,
      prefix: this.prefix,
      kernel: this.kernelVersion,
    })
  }

  private async handleReq(id: string, op: string, a: Record<string, unknown>): Promise<void> {
    if (!this.protocol) {
      this.replyErr(id, busError('E_UNSUPPORTED', '尚未完成握手', { hint: '請先送出 hello。' }))
      return
    }

    try {
      switch (op) {
        case 'register':
          this.replyOk(id, this.opRegister(a as unknown as RegisterArgs))
          return
        case 'replace_nodes':
          this.requireRegistration().replaceNodes(a['nodes'] as NodeDef[])
          this.replyOk(id, null)
          return
        case 'add_nodes':
          this.requireRegistration().addNodes(a['nodes'] as NodeDef[])
          this.replyOk(id, null)
          return
        case 'remove_nodes':
          this.requireRegistration().removeNodes(a['paths'] as string[])
          this.replyOk(id, null)
          return
        case 'unregister':
          this.requireRegistration().unregister(a['reason'] as string | undefined)
          this.registration = null
          this.replyOk(id, null)
          return
        case 'read':
          this.replyOk(id, this.bus.read(String(a['path'])) ?? null)
          return
        case 'read_many':
          this.replyOk(id, this.bus.readMany(String(a['pattern'])))
          return
        case 'write': {
          const args = a as unknown as WriteArgs
          // origin 由這裡填入，app 送什麼都不採信——身分不能自己宣告。
          // gesture 相反：只有 app 那份文件知道使用者有沒有動作，
          // 所以由 app 宣稱。它只用於顯示，風險閘門一律不看它。
          const result: WriteResult = await this.bus.write(args.path, args.value, {
            origin: this.uid,
            gesture: args.gesture === true,
            ...(args.timeout === undefined ? {} : { timeout: args.timeout }),
          })
          if (result.ok) this.replyOk(id, { value: result.value })
          else this.replyErr(id, result.error)
          return
        }
        case 'call': {
          const args = a as unknown as CallArgs
          const result: CallResult = await this.bus.call(args.path, args.args, {
            origin: this.uid,
            gesture: args.gesture === true,
            ...(args.timeout === undefined ? {} : { timeout: args.timeout }),
          })
          if (result.ok) this.replyOk(id, { result: result.result })
          else this.replyErr(id, result.error)
          return
        }
        case 'subscribe': {
          const sub = this.nextSubId++
          const unsubscribe = this.bus.subscribe(String(a['pattern']), (m) => {
            this.send({ qd: WIRE_MAGIC, t: 'msg', sub, m })
          })
          this.subs.set(sub, unsubscribe)
          this.replyOk(id, { sub })
          return
        }
        case 'unsubscribe': {
          const sub = Number(a['sub'])
          this.subs.get(sub)?.()
          this.subs.delete(sub)
          this.replyOk(id, null)
          return
        }
        default:
          this.replyErr(
            id,
            busError('E_UNSUPPORTED', `未知的 op：${op}`, {
              hint: '請確認 SDK 與桌面的協定版本相容。',
            }),
          )
      }
    } catch (cause) {
      this.replyErr(id, toBusError(cause))
    }
  }

  private handleNote(op: string, a: Record<string, unknown>): void {
    const registration = this.registration
    if (!registration) {
      this.fault(busError('E_NO_OWNER', `尚未註冊就送出 ${op}`, { hint: '請先送出 req register。' }), {
        op,
      })
      return
    }
    try {
      if (op === 'publish') {
        registration.publish(String(a['path']), a['value'])
        return
      }
      if (op === 'emit') {
        registration.emit(String(a['path']), a['payload'])
        return
      }
      if (op === 'touch') {
        this.onTouch?.()
        return
      }
      if (op === 'gesture') {
        const phase = String(a['phase'])
        if (phase !== 'start' && phase !== 'move' && phase !== 'end') {
          this.fault(busError('E_SCHEMA', `未知的手勢階段：${phase}`), { op })
          return
        }
        const raw = Array.isArray(a['points']) ? a['points'] : []
        const points: GesturePoint[] = []
        for (const p of raw) {
          const x = (p as { x?: unknown }).x
          const y = (p as { y?: unknown }).y
          if (typeof x !== 'number' || typeof y !== 'number') continue
          if (!Number.isFinite(x) || !Number.isFinite(y)) continue
          points.push({ x, y })
        }
        this.onGesture?.(phase, points)
        return
      }
      this.fault(busError('E_UNSUPPORTED', `未知的 note op：${op}`), { op })
    } catch (cause) {
      // §6.2 射後不理的錯誤以 fault 非同步送回，不得靜默吞掉
      this.fault(toBusError(cause), { op, path: a['path'] })
    }
  }

  // ─────────────────────────────── 操作

  private opRegister(args: RegisterArgs): { prefix: string } {
    if (this.registration) {
      throw busError('E_DUPLICATE', `實例 ${this.uid} 已在此連線上註冊過`, {
        hint: '一個 Port 只能註冊一次。重載請建立新的 Port。',
      })
    }
    this.registration = this.bus.register({
      uid: this.uid,
      title: args.title,
      summary: args.summary,
      nodes: args.nodes,
      ...(args.groups ? { groups: args.groups } : {}),
      ...(args.initial_window
        ? { initial_window: args.initial_window as { rect?: never } }
        : {}),
    })

    // `from` 一路送到 iframe 裡。它是傳輸層填的，app 偽造不了自己的來電顯示。
    this.registration.onWrite(async (relPath, value, from): Promise<WriteOutcome> => {
      const result = await this.hostRequest(
        'on_write',
        { path: relPath, value, from },
        ON_WRITE_TIMEOUT_MS,
      )
      return normalizeOutcome(result, relPath)
    })

    this.registration.onCall(async (relPath, callArgs, from) =>
      this.hostRequest('on_call', { path: relPath, args: callArgs, from }, ON_CALL_TIMEOUT_MS),
    )

    return { prefix: this.prefix }
  }

  private requireRegistration(): Registration {
    if (!this.registration) {
      throw busError('E_NO_OWNER', '尚未註冊', { hint: '請先送出 req register。' })
    }
    return this.registration
  }

  // ─────────────────────────────── Host → App 請求

  private hostRequest(
    op: string,
    a: Record<string, unknown>,
    timeoutMs: number,
  ): Promise<unknown> {
    if (this.detached) {
      return Promise.reject(new Error('連線已中斷'))
    }
    const id = `h${this.nextReqId++}`
    return new Promise<unknown>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id)
        reject(new Error(`app 未在 ${timeoutMs} ms 內回應 ${op}`))
      }, timeoutMs)
      this.pending.set(id, { resolve, reject, timer })
      this.send({ qd: WIRE_MAGIC, t: 'req', id, op, a })
    })
  }

  private settle(id: string, error: BusError | null, value: unknown): void {
    const entry = this.pending.get(id)
    if (!entry) return
    this.pending.delete(id)
    clearTimeout(entry.timer)
    if (error) entry.reject(new Error(error.message))
    else entry.resolve(value)
  }

  // ─────────────────────────────── 送訊

  private send(frame: Frame): void {
    if (this.detached && frame.t !== 'bye') return
    this.port.post(frame)
  }

  private replyOk(id: string, r: unknown): void {
    this.send({ qd: WIRE_MAGIC, t: 'ok', id, r })
  }

  private replyErr(id: string, e: BusError): void {
    this.send({ qd: WIRE_MAGIC, t: 'err', id, e })
  }

  private fault(e: BusError, ctx?: unknown): void {
    this.send({ qd: WIRE_MAGIC, t: 'fault', e, ...(ctx === undefined ? {} : { ctx }) })
    this.onFault?.(e)
  }

  // ─────────────────────────────── 斷線

  private detach(reason: string): void {
    if (this.detached) return
    this.detached = true

    for (const unsubscribe of this.subs.values()) unsubscribe()
    this.subs.clear()

    for (const [, entry] of this.pending) {
      clearTimeout(entry.timer)
      entry.reject(new Error(reason))
    }
    this.pending.clear()

    // §8.2 Port 關閉即視為崩潰：$window/* 與 $meta 保留，視窗框留在原位
    if (this.registration) {
      this.bus.markCrashed(this.uid, reason)
      this.registration = null
    }
  }
}

// ─────────────────────────────────────────────── 輔助

function toBusError(cause: unknown): BusError {
  if (typeof cause === 'object' && cause !== null && 'code' in cause && 'message' in cause) {
    return cause as BusError
  }
  if (cause instanceof Error && 'error' in cause) {
    return (cause as { error: BusError }).error
  }
  return busError('E_INTERNAL', String(cause))
}

/** App 回傳的東西不可信，正規化成 WriteOutcome，形狀不對就當成拒絕 */
function normalizeOutcome(result: unknown, relPath: string): WriteOutcome {
  if (typeof result === 'object' && result !== null && 'accept' in result) {
    const outcome = result as { accept: unknown; value?: unknown; reason?: unknown }
    if (outcome.accept === true) {
      return outcome.value === undefined ? { accept: true } : { accept: true, value: outcome.value }
    }
    if (outcome.accept === false) {
      return { accept: false, reason: String(outcome.reason ?? '擁有者拒絕，未說明原因') }
    }
  }
  throw new Error(`app 對 ${relPath} 的寫入回傳了不合法的 WriteOutcome`)
}
